import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import useProfileImage from '../../hooks/useProfileImage';
import { 
  Home, Calendar, CreditCard, Bell, Users,
  Menu, X, LogOut, Settings, ChevronDown, UserCircle, RefreshCw,
  ClipboardList, ScanLine, MessageSquare, Camera, MoreHorizontal
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import NotificationBell from '../notifications/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientLayout = ({ children }) => {
  const { user, event, logout, permissions, token } = useAuth();
  const profileImageUrl = useProfileImage(user);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [eventInfo, setEventInfo] = useState(event);
  const [logoUrl, setLogoUrl] = useState(null);

  // Check committee permissions
  const canManageLogistics = permissions?.includes('manage_logistics');
  const canManageCheckin = permissions?.includes('manage_attendance');

  useEffect(() => {
    if (!event && user?.event_id) {
      fetchEventInfo();
    }
  }, [event, user]);

  const fetchEventInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/info`);
      setEventInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch event info:', error);
    }
  };

  // Fetch event logo from cloud storage
  useEffect(() => {
    const ev = event || eventInfo;
    const fileId = ev ? ev.logo_file_id : null;
    if (fileId && token) {
      axios.get(`${API_URL}/api/files/download/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }).then(res => {
        setLogoUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(res.data);
        });
      }).catch(() => {});
    }
  }, [event, eventInfo, token]);

  // Base navigation items
  const baseNavigation = [
    { name: 'Home', href: '/attendee', icon: Home },
    { name: 'Announcements', href: '/attendee/announcements', icon: Bell },
    { name: 'Event Program', href: '/attendee/program', icon: Calendar },
    { name: 'My Payments', href: '/attendee/payments', icon: CreditCard },
    { name: 'Gallery', href: '/attendee/gallery', icon: Camera },
    { name: 'Batchmates', href: '/attendee/batchmates', icon: Users },
    { name: 'Messages', href: '/attendee/messages', icon: MessageSquare },
  ];

  // Add committee-specific navigation items
  const navigation = [
    ...baseNavigation,
    // Logistics committee gets Tasks link
    ...(canManageLogistics ? [{ 
      name: 'Tasks', 
      href: '/attendee/tasks', 
      icon: ClipboardList,
      badge: 'Logistics'
    }] : []),
    // Registration committee gets Check-in link
    ...(canManageCheckin ? [{ 
      name: 'Check-in', 
      href: '/attendee/checkin', 
      icon: ScanLine,
      badge: 'Registration'
    }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  // Use event from context or state
  const currentEvent = event || eventInfo;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream to-cream-paper">
      {/* Event Banner with Utility Actions */}
      {currentEvent && (
        <div className="bg-maroon text-white py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoUrl && (
                <img src={logoUrl} alt="Event Logo" className="h-6 w-6 rounded-full object-cover border border-white/30" />
              )}
              <p className="text-sm font-medium">
                <span className="font-heading">{currentEvent.school_name}</span>
                {currentEvent.event_name && (
                  <>
                    <span className="mx-2">•</span>
                    <span className="text-gold">{currentEvent.event_name}</span>
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell triggerClassName="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8" />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.location.reload()}
                className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8"
                data-testid="refresh-page-btn"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/attendee" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-maroon flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">BT</span>
              </div>
              <div className="hidden sm:block">
                <h1 className="font-heading font-bold text-lg text-maroon">batchm8s</h1>
                <p className="text-xs text-gray-500">Attendee Portal</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navigation.slice(0, 7).map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all text-sm whitespace-nowrap ${
                      isActive 
                        ? 'bg-maroon text-white' 
                        : item.badge 
                          ? 'text-gold-foreground bg-gold/10 hover:bg-gold/20 border border-gold/30' 
                          : 'text-gray-600 hover:bg-cream-paper hover:text-maroon'
                    }`}
                    data-testid={`client-nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    <span className="font-medium">{item.name}</span>
                    {item.badge && !isActive && (
                      <Badge className="text-[10px] px-1.5 py-0 bg-gold text-gray-900 ml-1">
                        {item.badge}
                      </Badge>
                    )}
                  </Link>
                );
              })}
              {navigation.length > 7 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="px-3 py-2 rounded-full">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {navigation.slice(7).map((item) => (
                      <DropdownMenuItem key={item.name} asChild>
                        <Link to={item.href} className="flex items-center gap-2">
                          <item.icon className="h-4 w-4" />
                          {item.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {/* Fallback: show bell & refresh in header when no event banner */}
              {!currentEvent && (
                <>
                  <NotificationBell />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => window.location.reload()}
                    className="text-gray-500 hover:text-maroon hover:bg-cream-paper"
                    data-testid="refresh-page-btn-fallback"
                  >
                    <RefreshCw className="h-5 w-5" />
                  </Button>
                </>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 p-2 rounded-full hover:bg-cream-paper transition-all">
                    <Avatar className="h-9 w-9 border-2 border-gold">
                      <AvatarImage src={profileImageUrl || user?.profile_photo} />
                      <AvatarFallback className="bg-maroon text-white font-medium text-sm">
                        {getInitials(user?.first_name, user?.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden sm:block text-left">
                      <p className="font-medium text-sm text-gray-900">
                        {user?.first_name}
                      </p>
                      {user?.batch_year && (
                        <p className="text-xs text-gray-500">Batch {user.batch_year}</p>
                      )}
                    </div>
                    <ChevronDown className="h-4 w-4 text-gray-400 hidden sm:block" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 sm:hidden">
                    <p className="font-medium text-sm">{user?.first_name} {user?.last_name}</p>
                    {user?.batch_year && (
                      <p className="text-xs text-gray-500">Batch {user.batch_year}</p>
                    )}
                  </div>
                  <DropdownMenuSeparator className="sm:hidden" />
                  <DropdownMenuItem onClick={() => navigate('/attendee/profile')}>
                    <UserCircle className="mr-2 h-4 w-4" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden"
                data-testid="client-mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-16">
          <nav className="px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-maroon text-white' 
                      : item.badge
                        ? 'bg-gold/10 text-gray-900 border border-gold/30'
                        : 'text-gray-700 hover:bg-cream-paper'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="h-5 w-5" />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  {item.badge && !isActive && (
                    <Badge className="text-xs bg-gold text-gray-900">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t bg-white/50 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p>© 2025 batchm8s Reunion. Made with love for our batchmates.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientLayout;
